import { inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  ResolveFn,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { AuthService } from '@core/authentication/auth.service';
import { TenantFeaturesSettings } from '@core/models';
import { CLEAR_ROLES, CREDIT_REPORT_ROLES } from '@core/models/application-menu-roles';
import { TenantService } from '@core/services';
import { expandCollectionsRoleAccess } from '@shared/application-menu.utils';
import { catchError, map, Observable, of } from 'rxjs';
import { OiqWorkflowApplicationAccessOptions } from './application/application.models';
import { OiqWorkflowApplicationApplyAccess } from './application/application.workflow';
import { OIQ_WORKFLOW_DECISION_CONFIG } from './decision/decision.models';
import { OIQ_WORKFLOW_CONFIG } from './workflow.models';

const OIQ_WORKFLOW_ACCESS_DENIED: OiqWorkflowApplicationAccessOptions = {
  clearEnabled: false,
  creditEnabled: false,
  dataMerchEnabled: false,
};

const OiqWorkflowAccessFromFeatures = (
  features: TenantFeaturesSettings | null,
  userRoles: string[]
): OiqWorkflowApplicationAccessOptions => {
  const _hasRole = (roles: string[]): boolean =>
    expandCollectionsRoleAccess(roles, !!features?.ExpandCollectionsRolePermissions).some(_role =>
      userRoles.includes(_role)
    );

  return {
    clearEnabled: !!features?.Clear && _hasRole(CLEAR_ROLES),
    creditEnabled: !!features?.Crs && _hasRole(CREDIT_REPORT_ROLES),
    dataMerchEnabled: !!features?.DataMerch,
  };
};

@Injectable({
  providedIn: 'root',
})
export class WorkflowAccessGuard {
  private _auth = inject(AuthService);
  private _tenants = inject(TenantService);
  private _config = inject(OIQ_WORKFLOW_CONFIG);
  private _decisionConfig = inject(OIQ_WORKFLOW_DECISION_CONFIG);
  private _features: TenantFeaturesSettings | null = null;

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const _tenantId = this._auth.getUser()?.tenantId;
    if (!_tenantId) {
      return false;
    }

    return this._tenants.getSettings(_tenantId).pipe(
      catchError(() => of(false)),
      map((_settings: { features: TenantFeaturesSettings }) => {
        this._features = _settings?.features || null;
        return this._features?.Workflow ?? false;
      })
    );
  }

  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<OiqWorkflowApplicationAccessOptions>
    | Promise<OiqWorkflowApplicationAccessOptions>
    | OiqWorkflowApplicationAccessOptions {
    if (!this._features) {
      return of(OIQ_WORKFLOW_ACCESS_DENIED);
    }

    const _access = OiqWorkflowAccessFromFeatures(this._features, this._auth.userRoles);
    OiqWorkflowApplicationApplyAccess(this._config, this._decisionConfig, _access);
    return of(_access);
  }
}

export const workflowAccessResolverFn: ResolveFn<OiqWorkflowApplicationAccessOptions> = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const _workflowAccessGuard = inject(WorkflowAccessGuard);
  return _workflowAccessGuard.resolve(route, state);
};

export const workflowAccessGuardFn: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const _workflowAccessGuard = inject(WorkflowAccessGuard);
  return _workflowAccessGuard.canActivate(route, state);
};
